import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { logTaskActivity, TaskActivityType } from '@/utils/taskActivityLogger';

// Define types for our shortcut commands
export interface CommandShortcut {
  id: number;
  code: string;
  name: string;
  description?: string;
  entity_type: 'task' | 'contact' | 'all';
  action_type: string;
  parameters?: {
    pattern?: string;
    values?: string[];
    example?: string;
    [key: string]: any;
  };
}

interface CommandAction {
  type: string;
  value: string;
  originalCommand: string;
}

/**
 * Parses a shortcut string into individual commands
 * @param shortcutString The shortcut string to parse (e.g., "5 p:high s:active")
 * @returns Array of parsed commands and their values
 */
export const parseShortcutCommands = async (shortcutString: string): Promise<CommandAction[]> => {
  try {
    // Get all registered shortcuts from the database
    const { data: registeredShortcuts, error } = await supabase
      .from('command_shortcuts')
      .select('*');
    
    if (error) {
      console.error('Error fetching registered shortcuts:', error);
      return [];
    }
    
    const shortcuts = registeredShortcuts as CommandShortcut[];
    const commands: CommandAction[] = [];
    
    // Output what we received for debugging purposes
    console.log('Registered shortcuts:', shortcuts);
    
    // Split the input by spaces to separate multiple commands
    const commandParts = shortcutString.trim().split(/\s+/);
    console.log('Command parts:', commandParts);
    
    for (const part of commandParts) {
      let matched = false;
      
      // Check against each registered shortcut pattern
      for (const shortcut of shortcuts) {
        // Skip if no pattern is defined
        if (!shortcut.parameters?.pattern) continue;
        
        const pattern = new RegExp(shortcut.parameters.pattern);
        console.log(`Testing "${part}" against pattern "${shortcut.parameters.pattern}"`);
        
        if (pattern.test(part)) {
          matched = true;
          console.log(`Match found! Shortcut: ${shortcut.name}, Type: ${shortcut.action_type}`);
          
          // Extract the value based on the action type
          let value = '';
          
          switch (shortcut.action_type) {
            case 'date':
              // For date shortcuts, extract the number of days
              if (shortcut.code === '\\d+') {
                // Direct number (e.g., "5")
                value = part;
                console.log(`Extracted date value: ${value} (direct number)`);
              } else if (shortcut.code === 'd:') {
                // d:X format (e.g., "d:5")
                value = part.substring(2);
                console.log(`Extracted date value: ${value} (d: format)`);
              }
              break;
              
            case 'priority':
              // For priority shortcuts (e.g., "p:high")
              value = part.substring(2).toLowerCase();
              console.log(`Extracted priority value: ${value}`);
              break;
              
            case 'stage':
              // For stage shortcuts (e.g., "s:active")
              value = part.substring(2);
              console.log(`Extracted stage value: ${value}`);
              break;
              
            default:
              value = part;
              console.log(`Using default value extraction: ${value}`);
              break;
          }
          
          commands.push({
            type: shortcut.action_type,
            value,
            originalCommand: part
          });
          
          break;
        }
      }
      
      if (!matched && part.trim() !== '') {
        console.warn(`Unrecognized command: ${part}`);
      }
    }
    
    console.log('Parsed commands:', commands);
    return commands;
  } catch (err) {
    console.error('Error parsing shortcut commands:', err);
    return [];
  }
};

/**
 * Executes task-related shortcut commands
 * @param taskId The ID of the task
 * @param commands Array of parsed commands to execute
 */
export const executeTaskCommands = async (taskId: number, commands: CommandAction[]): Promise<boolean> => {
  try {
    if (!commands.length) return true;
    
    // Get current task data for logging changes
    const { data: taskData, error: taskError } = await supabase
      .from('tasks')
      .select('priority, stage, next_action_date')
      .eq('id', taskId)
      .single();
      
    if (taskError) {
      console.error('Error fetching task data:', taskError);
      return false;
    }
    
    const updates: Record<string, any> = {
      updated_at: new Date().toISOString()
    };
    
    // Process each command
    for (const command of commands) {
      switch (command.type) {
        case 'date':
          const days = parseInt(command.value);
          if (!isNaN(days)) {
            const targetDate = addDays(new Date(), days);
            updates.next_action_date = format(targetDate, "yyyy-MM-dd");
            
            // Log date change
            await logTaskActivity({
              taskId,
              activityType: 'date_change',
              fieldName: 'next_action_date',
              oldValue: taskData?.next_action_date ? new Date(taskData.next_action_date).toISOString() : '',
              newValue: targetDate.toISOString()
            });
          }
          break;
        
        case 'priority':
          const priority = command.value.charAt(0).toUpperCase() + command.value.slice(1);
          if (['Low', 'Medium', 'High', 'VIP'].includes(priority)) {
            updates.priority = priority;
            
            // Log priority change
            await logTaskActivity({
              taskId,
              activityType: 'field_update',
              fieldName: 'priority',
              oldValue: taskData?.priority || '',
              newValue: priority
            });
          }
          break;
        
        case 'stage':
          // Capitalize first letter of stage
          const stage = command.value.charAt(0).toUpperCase() + command.value.slice(1);
          updates.stage = stage;
          
          // Log stage change
          await logTaskActivity({
            taskId,
            activityType: 'stage_change',
            oldValue: taskData?.stage || '',
            newValue: stage
          });
          break;
      }
    }
    
    // If we have updates to apply, update the task
    if (Object.keys(updates).length > 1) { // More than just updated_at
      const { error: updateError } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', taskId);
        
      if (updateError) {
        console.error('Error applying task command updates:', updateError);
        toast.error('Failed to apply some shortcut commands');
        return false;
      }
      
      // Log the shortcut used
      await logTaskActivity({
        taskId,
        activityType: 'shortcut_added',
        newValue: commands.map(c => c.originalCommand).join(' ')
      });
      
      return true;
    }
    
    return true;
  } catch (err) {
    console.error('Error executing task commands:', err);
    toast.error('Error executing shortcut commands');
    return false;
  }
};

/**
 * Updates shortcut commands in the database
 */
export const updateShortcutCommand = async (command: Partial<CommandShortcut> & { id?: number }): Promise<boolean> => {
  try {
    // Ensure required fields are present based on whether it's an update or create
    if (command.id) {
      // Update existing command - we only need to include fields that are changing
      const { error } = await supabase
        .from('command_shortcuts')
        .update(command)
        .eq('id', command.id);
      
      if (error) {
        console.error('Error updating command shortcut:', error);
        toast.error('Failed to update shortcut command');
        return false;
      }
    } else {
      // Create new command - ensure all required fields are present
      if (!command.code || !command.name || !command.entity_type || !command.action_type) {
        console.error('Missing required fields for new command shortcut');
        toast.error('Missing required fields for new command');
        return false;
      }
      
      // Cast to required fields for new record
      const newCommand = {
        code: command.code,
        name: command.name,
        description: command.description || '',
        entity_type: command.entity_type,
        action_type: command.action_type,
        parameters: command.parameters || {}
      };
      
      const { error } = await supabase
        .from('command_shortcuts')
        .insert(newCommand);
      
      if (error) {
        console.error('Error creating command shortcut:', error);
        toast.error('Failed to create shortcut command');
        return false;
      }
    }
    
    return true;
  } catch (err) {
    console.error('Error with shortcut command operation:', err);
    toast.error('Error with shortcut command');
    return false;
  }
};

/**
 * Gets all registered shortcut commands
 */
export const getShortcutCommands = async (): Promise<CommandShortcut[]> => {
  try {
    const { data, error } = await supabase
      .from('command_shortcuts')
      .select('*')
      .order('entity_type', { ascending: true })
      .order('code', { ascending: true });
    
    if (error) {
      console.error('Error fetching shortcut commands:', error);
      toast.error('Failed to load shortcut commands');
      return [];
    }
    
    console.log('Fetched shortcut commands:', data);
    
    // If no commands found, create default commands
    if (!data || data.length === 0) {
      console.log('No commands found, creating defaults...');
      await createDefaultShortcuts();
      
      // Try fetching again
      const { data: refreshedData, error: refreshError } = await supabase
        .from('command_shortcuts')
        .select('*')
        .order('entity_type', { ascending: true })
        .order('code', { ascending: true });
        
      if (refreshError) {
        console.error('Error fetching shortcut commands after creating defaults:', refreshError);
        return [];
      }
      
      return refreshedData as CommandShortcut[] || [];
    }
    
    return data as CommandShortcut[];
  } catch (err) {
    console.error('Exception fetching shortcut commands:', err);
    toast.error('Error loading shortcut commands');
    return [];
  }
};

/**
 * Deletes a shortcut command
 */
export const deleteShortcutCommand = async (commandId: number): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('command_shortcuts')
      .delete()
      .eq('id', commandId);
    
    if (error) {
      console.error('Error deleting shortcut command:', error);
      toast.error('Failed to delete shortcut command');
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Exception deleting shortcut command:', err);
    toast.error('Error deleting shortcut command');
    return false;
  }
};

/**
 * Processes a shortcut string for a task
 */
export const processTaskShortcut = async (taskId: number, shortcutString: string): Promise<boolean> => {
  try {
    // Parse the commands
    const commands = await parseShortcutCommands(shortcutString);
    
    if (commands.length === 0) {
      console.log('No valid commands found in shortcut:', shortcutString);
      return false;
    }
    
    // Execute the commands
    return await executeTaskCommands(taskId, commands);
  } catch (err) {
    console.error('Error processing task shortcut:', err);
    toast.error('Error processing shortcut');
    return false;
  }
};

/**
 * Processes a contact shortcut similar to task shortcut
 * Note: This implementation is a stub for future expansion
 */
export const processContactShortcut = async (contactId: number, shortcutString: string): Promise<boolean> => {
  try {
    // Implement contact-specific command execution here
    const commands = await parseShortcutCommands(shortcutString);
    
    if (commands.length === 0) {
      console.log('No valid commands found in shortcut:', shortcutString);
      return false;
    }
    
    // Process commands for contacts (stub for now)
    console.log('Processing contact commands:', commands);
    return true;
  } catch (err) {
    console.error('Error processing contact shortcut:', err);
    toast.error('Error processing contact shortcut');
    return false;
  }
};

/**
 * Creates default shortcut commands if none exist
 */
export const createDefaultShortcuts = async (): Promise<boolean> => {
  try {
    console.log('Creating default shortcuts...');
    const defaultShortcuts: Omit<CommandShortcut, 'id'>[] = [
      // Number pattern for setting date (e.g., "5")
      {
        code: '\\d+',
        name: 'Set Date (Number)',
        description: 'Set next action date to X days from today using just a number (e.g. "5")',
        entity_type: 'task',
        action_type: 'date',
        parameters: {
          pattern: '^\\d+$',
          example: '5'
        }
      },
      // d:X pattern for setting date (e.g., "d:5")
      {
        code: 'd:',
        name: 'Set Date (d:X)',
        description: 'Set next action date to X days from today (e.g. "d:5")',
        entity_type: 'task',
        action_type: 'date',
        parameters: {
          pattern: '^d:\\d+$',
          example: 'd:5'
        }
      },
      // p:X pattern for setting priority (e.g., "p:high")
      {
        code: 'p:',
        name: 'Set Priority',
        description: 'Set task priority (e.g. "p:high")',
        entity_type: 'task',
        action_type: 'priority',
        parameters: {
          pattern: '^p:(low|medium|high|vip)$',
          values: ['low', 'medium', 'high', 'vip'],
          example: 'p:high'
        }
      },
      // s:X pattern for setting stage (e.g., "s:active")
      {
        code: 's:',
        name: 'Set Stage',
        description: 'Set task stage (e.g. "s:active")',
        entity_type: 'task',
        action_type: 'stage',
        parameters: {
          pattern: '^s:\\w+$',
          values: ['active', 'completed', 'blocked', 'deferred', 'next'],
          example: 's:active'
        }
      }
    ];
    
    // Insert the default shortcuts
    const { error } = await supabase
      .from('command_shortcuts')
      .insert(defaultShortcuts);
      
    if (error) {
      console.error('Error creating default shortcuts:', error);
      toast.error('Failed to create default shortcut commands');
      return false;
    }
    
    console.log('Default shortcuts created successfully');
    return true;
  } catch (err) {
    console.error('Exception creating default shortcuts:', err);
    toast.error('Error creating default shortcuts');
    return false;
  }
};
